import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Grid, Table, TableBody, TableHead, Box, TableRow, TableCell, Tooltip, Typography, CircularProgress
} from '@mui/material';
import { RiListSettingsLine, RiPencilLine } from '@remixicon/react';

import PrimaryActionBtn from 'src/shared/buttons/PrimaryActionBtn';
import theme from 'src/themeNew';
import styles from './style';

type PropType = {
  loading: boolean;
  workflows: Record<string, any>[];
  onEdit: (_workflow: Record<string, any>) => void;
  onManageFields: (_workflow: Record<string, any>) => void;
}

const WorkflowsTable = (props: PropType) => {
  const { ready, t } = useTranslation();
  const { loading, workflows, onEdit, onManageFields } = props;

  const getInitials = (email: string): string => {
    return email.slice(0, 2).toUpperCase();
  };

  const renderUserAvatars = (users: string[] = []) => {
    if (!users || users.length === 0) {
      return (
        <Typography sx={styles.text}>
          -
        </Typography>
      );
    }

    const maxVisible = 5;
    const visibleUsers = users.slice(0, maxVisible);
    const remainingCount = users.length - maxVisible;

    return (
      <Box sx={styles.avatarContainer}>
        {visibleUsers.map((user, index) => (
          <Tooltip key={index} title={user} arrow>
            <Box sx={styles.avatarBall}>
              {getInitials(user)}
            </Box>
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Tooltip
            title={users.slice(maxVisible).join(', ')}
            arrow
          >
            <Box sx={styles.avatarMore}>
              ...
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  const defaultChip = () => (
    <Box sx={styles.defaultChip}>
      <Typography sx={styles.defaultLabel}>
        {ready && t('DOC_TYPES_DEFAULT_TAG')}
      </Typography>
    </Box>
  );

  if (loading) return (
    <Grid sx={styles.contentWrapper}>
      <CircularProgress />
    </Grid>
  );

  return (
    <Grid sx={styles.tableWrapper} className="xy-scroll">
      {workflows.length > 0 ?
        (
          <Table size="small" sx={styles.table}>
            <TableHead>
              <TableRow sx={styles.tableHeadRow}>
                <TableCell sx={{ ...styles.tableHeadRowDisplay, width: '20%' }}>
                  {ready && t('WORKFLOW_TABLE_PROJECT')}
                </TableCell>
                <TableCell sx={{ ...styles.tableHeadRowDisplay, width: '20%' }}>
                  {ready && t('WORKFLOW_TABLE_WORKFLOW')}
                </TableCell>
                <TableCell sx={{ ...styles.tableHeadRowDisplay, width: '30%' }}>
                  {ready && t('WORKFLOW_TABLE_DESCRIPTION')}
                </TableCell>
                <TableCell sx={{ ...styles.tableHeadRowDisplay, width: '20%' }}>
                  {ready && t('WORKFLOW_TABLE_ASSIGNED_USERS')}
                </TableCell>
                <TableCell sx={{ ...styles.tableHeadRowDisplay, width: '10%', borderImage: 'none' }}>
                  {ready && t('DOC_TYPES_TABLE_ACTION')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workflows.map((workflow: Record<string, any>) =>
                <TableRow key={workflow.id} sx={styles.tableBodyRow}>
                  <TableCell sx={{ ...styles.tableRowDisplay, maxWidth: '200px' }}>
                    <Box sx={styles.tableCellRow}>
                      <Tooltip title={workflow.name}>
                        <Typography sx={styles.text}>
                          {workflow.name}
                        </Typography>
                      </Tooltip>
                      {workflow.isDefault && defaultChip()}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...styles.tableRowDisplay, maxWidth: '200px' }}>
                    <Box sx={styles.tableCellRow}>
                      <Tooltip title={workflow.workflowName || 'No workflow assigned'}>
                        <Typography sx={styles.text}>
                          {workflow.workflowName || '-'}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...styles.tableRowDisplay, maxWidth: '300px' }}>
                    <Tooltip title={workflow.workflowDescription || workflow.description || 'No description'}>
                      <Typography sx={styles.text}>
                        {workflow.workflowDescription || workflow.description || '-'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ ...styles.tableRowDisplay, maxWidth: '200px' }}>
                    {renderUserAvatars(workflow.users)}
                  </TableCell>
                  <TableCell sx={styles.tableRowDisplay}>
                    <Box sx={styles.actionIconContainer}>
                      <PrimaryActionBtn
                        title={t('DOC_TYPES_TABLE_EDIT')}
                        onClick={() => onEdit(workflow)}
                        startIcon={<RiPencilLine color={theme.palette.primary.main} size="20" />}
                      />
                      <PrimaryActionBtn
                        title={t('WORKFLOWS_TABLE_MANAGE_WORKFLOW')}
                        onClick={() => onManageFields(workflow)}
                        startIcon={<RiListSettingsLine color={theme.palette.primary.main} size="20" />}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        ) :
        <Grid sx={styles.contentWrapper}>
          <Typography sx={styles.emptyText}>{t('DOC_TYPES_TABLE_EMPTY')}</Typography>
        </Grid>}
    </Grid>
  )
}

export default WorkflowsTable
